import type { ChildProcess } from 'node:child_process';
import type WebSocket from 'ws';
import type { StreamTail } from './stream-tail.js';
import type { BridgePreviewEnvironmentDef, PreviewEnvironmentLogTails } from './types.js';
import { sendPreviewEnvironmentStatus } from '../process/send-preview-environment-status.js';

/** In-memory maps tracking configured preview environments and their local child processes. */
export type PreviewEnvironmentManagerState = {
  defsById: Map<string, BridgePreviewEnvironmentDef>;
  processes: Map<string, ChildProcess>;
  streamTailsByServerId: Map<string, { stdout: StreamTail; stderr: StreamTail }>;
  spawnGenerationByServerId: Map<string, number>;
  pipedCaptureByServerId: Map<string, boolean>;
  mergedLogPollByServerId: Map<string, ReturnType<typeof setInterval>>;
  mergedLogCleanupDirByServerId: Map<string, string>;
  abortControllersByServerId: Map<string, AbortController>;
};

export function createPreviewEnvironmentManagerState(): PreviewEnvironmentManagerState {
  return {
    defsById: new Map(),
    processes: new Map(),
    streamTailsByServerId: new Map(),
    spawnGenerationByServerId: new Map(),
    pipedCaptureByServerId: new Map(),
    mergedLogPollByServerId: new Map(),
    mergedLogCleanupDirByServerId: new Map(),
    abortControllersByServerId: new Map(),
  };
}

export function previewEnvironmentDisplayTitle(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): string {
  const name = state.defsById.get(environmentId)?.name.trim();
  return name || environmentId.slice(0, 8);
}

export function emptyPreviewEnvironmentLogTails(): PreviewEnvironmentLogTails {
  return { stdout: [], stderr: [] };
}

export function snapshotPreviewEnvironmentTails(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): PreviewEnvironmentLogTails {
  const tails = state.streamTailsByServerId.get(environmentId);
  if (!tails) return emptyPreviewEnvironmentLogTails();
  return { stdout: tails.stdout.getTail(), stderr: tails.stderr.getTail() };
}

export function clearPreviewEnvironmentTails(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): void {
  state.streamTailsByServerId.delete(environmentId);
}

export function bumpPreviewEnvironmentSpawnGeneration(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): void {
  const nextGen = (state.spawnGenerationByServerId.get(environmentId) ?? 0) + 1;
  state.spawnGenerationByServerId.set(environmentId, nextGen);
}

export function clearPreviewEnvironmentPoll(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): void {
  const poll = state.mergedLogPollByServerId.get(environmentId);
  if (poll) {
    clearInterval(poll);
    state.mergedLogPollByServerId.delete(environmentId);
  }
}

export function abortPreviewEnvironmentSpawn(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): void {
  state.abortControllersByServerId.get(environmentId)?.abort();
  state.abortControllersByServerId.delete(environmentId);
}

/** Drop spawn abort handle without signalling — use when the child is stopped via tree-kill. */
export function discardPreviewEnvironmentAbortController(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): void {
  state.abortControllersByServerId.delete(environmentId);
}

export function sendPreviewEnvironmentManagerStatus(
  getWs: () => WebSocket | null,
  environmentId: string,
  status: string,
  detail?: string,
  tails?: PreviewEnvironmentLogTails,
): void {
  sendPreviewEnvironmentStatus(getWs, environmentId, status, { detail, tails });
}
